import { useMemo, useState } from 'react'
import {
  ComcastErrorType,
  triggerSentryError,
} from '../../../../../shared/helpers'
import {
  getStationsByAuto,
  getStationsByIpDefault,
  getStationsByZip,
} from '../../../actions/contentActions'
import { setUserStationById } from '../../../lib/utils/station/stationUtils'
import { SearchErrorEnum, StationData } from '../types'

export const useChangeStation = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isSearching, setIsSearching] = useState(false)
  const [stationsList, setStationsList] = useState<StationData[]>([])
  const [errorOnSearch, setErrorOnSearch] = useState<SearchErrorEnum | null>(
    null
  )

  const hasStations = useMemo(() => stationsList.length > 0, [stationsList])

  const getStationsByZipCode = async () => {
    const locationResponse = await getStationsByAuto()
    try {
      const stations = await getStationsByZip(locationResponse.zipcode)
      return stations
    } catch (err: any) {
      const errorDescription = `Error ${
        err.status || '(unknown)'
      } on GA Change Station screen getting default stations by IP`

      triggerSentryError('getStationsByZip', 'ChangeStationScreen.tsx', {
        errorMessage: err.message ?? 'Undefined error message (default)',
        errorDescription,
        errorLocation:
          'ChangeStationScreen - useChangeStation - getStationsByZipCode - getStationsByZip',
        errorType: ComcastErrorType.network,
      })
    } finally {
      setIsLoading(false)
      setIsSearching(false)
    }
  }

  const getStationsByUserIp = async () => {
    try {
      const stations = await getStationsByIpDefault()
      return stations
    } catch (err: any) {
      const errorDescription = `Error ${
        err.status ? err.status : '(unknown)'
      } on GA Change Station screen getting default stations by IP`

      triggerSentryError('getStationsByIpDefault', 'ChangeStationScreen.tsx', {
        errorMessage: err.message ?? 'Undefined error message (default)',
        errorDescription,
        errorLocation:
          'ChangeStationScreen - useChangeStation - getStationsByUserIp',
        errorType: ComcastErrorType.network,
      })
    }
  }

  const getInitialStations = async (showSpinner: boolean = true) => {
    if (showSpinner) setIsLoading(true)

    setIsSearching(true)
    setStationsList([])
    try {
      const { stations } = await getStationsByZipCode()
      setStationsList(stations)
    } catch (err: any) {
      const errorDescription = `Error ${
        err.status ? err.status : '(unknown)'
      } on GA Change Station screen getting default stations by 'auto' API`

      triggerSentryError('getStationsByAuto', 'ChangeStationScreen.tsx', {
        errorMessage: err.message ?? 'Undefined error message (default)',
        errorDescription,
        errorLocation:
          'ChangeStationScreen - useChangeStation - getInitialStations - getStationsByZipCode',
        errorType: ComcastErrorType.network,
      })

      const { stations } = await getStationsByUserIp()
      setStationsList(stations)
    } finally {
      setIsLoading(false)
      setIsSearching(false)
    }
  }

  const searchByZipCode = async (zipCode: string) => {
    setIsSearching(true)
    setErrorOnSearch(null)
    try {
      const { stations } = await getStationsByZip(zipCode)
      setStationsList(stations)
    } catch (err: any) {
      setStationsList([])
      if (err.status && err.status !== 404) {
        setErrorOnSearch(SearchErrorEnum.GENERIC)
      } else {
        setErrorOnSearch(SearchErrorEnum.NOT_FOUND)
      }
    } finally {
      setIsSearching(false)
    }
  }

  const setUserStation = async (station: StationData) => {
    const { pbs_id } = station
    setIsLoading(true)

    const result = await setUserStationById(pbs_id)

    setIsLoading(false)
    return result
  }

  return {
    isLoading,
    isSearching,
    stationsList,
    getInitialStations,
    hasStations,
    errorOnSearch,
    searchByZipCode,
    setUserStation,
  }
}
